import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DownloadSimple, FileText } from "@phosphor-icons/react";
import api, { formatErr, fmtDate, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ReportCard from "@/components/ReportCard";

const STATUS_STYLE = {
  present: "bg-status-green/10 text-status-green",
  absent: "bg-status-red/10 text-status-red",
  leave: "bg-status-amber/20 text-amber-700",
};

function Tile({ label, value, testId }) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-white" data-testid={testId}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="font-heading text-2xl font-extrabold mt-1">{value}</p>
    </div>
  );
}

function Section({ title, children, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={testId}>
      <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider mb-3">{title}</h2>
      {children}
    </section>
  );
}

export default function Student360View({ studentId, variant }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [receiptBusy, setReceiptBusy] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    setFailed(false);
    try {
      const { data } = await api.get(`/school/students/${studentId}/360`);
      setData(data);
    } catch (e) {
      setFailed(true);
      toast.error(formatErr(e, t("student360.loadFailed", { defaultValue: "Could not load student profile" })));
    }
  }, [studentId, t]);

  useEffect(() => { load(); }, [load]);

  const downloadReceipt = async (paymentId) => {
    setReceiptBusy(paymentId);
    try {
      const res = await api.get(`/parent/fees/receipt/payment/${paymentId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${paymentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, t("parentFees.receiptUnavailable", { defaultValue: "Receipt not available yet" })));
    } finally {
      setReceiptBusy(null);
    }
  };

  if (failed) return <p className="text-sm text-slate-400 text-center py-10" data-testid="student360-error">{t("student360.loadFailed", { defaultValue: "Could not load student profile" })}</p>;
  if (!data) return <div className="grid place-items-center py-16" data-testid="student360-loading"><div className="w-8 h-8 rounded-full border-4 border-brand-coral border-t-transparent animate-spin" /></div>;

  const { student, class: cls, parent, attendance, fees, leaves, exam_results, subjects_teachers, timetable, academic_year } = data;
  const initials = (student?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const days = t("student360.days", { defaultValue: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], returnObjects: true });
  const timetableDays = [...new Set((timetable || []).map((s) => s.day_of_week))].sort((a, b) => a - b);

  return (
    <div className="space-y-4" data-testid="student360-root">
      {/* Overview */}
      <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="student360-overview">
        <div className="flex items-center gap-4">
          <span className="w-14 h-14 rounded-full bg-brand-coral/10 text-brand-coral grid place-items-center font-heading text-lg font-extrabold shrink-0">{initials}</span>
          <div className="min-w-0">
            <p className="font-heading text-lg font-extrabold truncate" data-testid="student360-name">{student?.name}</p>
            <p className="text-xs text-slate-500">{cls?.name || "—"}{student?.roll_no ? ` · ${t("student360.rollNo", { defaultValue: "Roll No" })} ${student.roll_no}` : ""}</p>
            {academic_year?.name && <p className="text-xs text-slate-500">{t("student360.academicYear", { defaultValue: "Academic Year" })}: {academic_year.name}</p>}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-4 text-sm">
          {student?.student_reference_id && <p className="col-span-2"><span className="text-slate-500">{t("student360.admissionId", { defaultValue: "Admission ID" })}:</span> <span className="font-mono text-xs" data-testid="student360-ref-id">{student.student_reference_id}</span></p>}
          {student?.dob && <p><span className="text-slate-500">{t("student360.dob", { defaultValue: "Date of Birth" })}:</span> {fmtDate(student.dob)}</p>}
          {parent?.name && <p><span className="text-slate-500">{t("student360.parentGuardian", { defaultValue: "Parent / Guardian" })}:</span> {parent.name}</p>}
        </div>
      </section>

      {/* Academics */}
      {subjects_teachers?.length > 0 && (
        <Section title={t("student360.subjectsTeachers", { defaultValue: "Subjects & Teachers" })} testId="student360-academics">
          <div className="space-y-2">
            {subjects_teachers.map((item, i) => (
              <div key={i} className="flex justify-between gap-4 p-3 rounded-lg bg-slate-50 text-sm">
                <span className="font-semibold">{item.subject_name}</span>
                <span className="text-slate-600">{item.teacher_name}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Timetable */}
      {timetable?.length > 0 && (
        <Section title={t("student360.timetable", { defaultValue: "Timetable" })} testId="student360-timetable">
          <div className="space-y-3">
            {timetableDays.map((d) => (
              <div key={d}>
                <p className="text-xs font-extrabold text-slate-500 uppercase tracking-wide mb-1.5">{days[d] || d}</p>
                <div className="space-y-1.5">
                  {timetable.filter((s) => s.day_of_week === d).sort((a, b) => a.period_number - b.period_number).map((s) => (
                    <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 text-sm">
                      <span className="text-[10px] font-bold text-slate-400 w-8 shrink-0">P{s.period_number}</span>
                      <span className="font-semibold flex-1 min-w-0 truncate">{s.subject_name}</span>
                      <span className="text-xs text-slate-500 truncate">{s.teacher_name}{s.room_name ? ` · ${s.room_name}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Attendance */}
      <Section title={t("student360.attendance", { defaultValue: "Attendance" })} testId="student360-attendance">
        <div className="grid grid-cols-3 gap-2.5">
          <Tile label={t("student360.presentLast30", { defaultValue: "Present (30 days)" })} value={attendance?.present_30d ?? 0} testId="student360-present" />
          <Tile label={t("student360.absentLast30", { defaultValue: "Absent (30 days)" })} value={attendance?.absent_30d ?? 0} testId="student360-absent" />
          <Tile label={t("student360.attendancePct", { defaultValue: "Attendance %" })} value={attendance?.percentage_30d != null ? `${attendance.percentage_30d}%` : "—"} testId="student360-pct" />
        </div>
        {attendance?.records?.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs text-slate-400 font-semibold">{t("student360.recentDays", { defaultValue: "Recent days" })}</p>
            {attendance.records.slice(0, 8).map((r, i) => (
              <div key={i} className="flex justify-between items-center text-sm p-2 rounded-lg bg-slate-50">
                <span>{fmtDate(r.date)}</span>
                <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-500"}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Leave */}
      {leaves?.length > 0 && (
        <Section title={t("student360.leave", { defaultValue: "Leave" })} testId="student360-leave">
          <div className="space-y-2">
            {leaves.map((l, i) => (
              <div key={i} className="p-3 rounded-lg bg-slate-50 text-sm">
                <div className="flex justify-between gap-2">
                  <span>{fmtDate(l.from_date)} → {fmtDate(l.to_date)}</span>
                  <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${l.status === "approved" ? STATUS_STYLE.present : l.status === "rejected" ? STATUS_STYLE.absent : STATUS_STYLE.leave}`}>{l.status}</span>
                </div>
                {l.reason && <p className="text-xs text-slate-500 mt-1">{l.reason}</p>}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Exams */}
      {exam_results?.length > 0 && (
        <Section title={t("student360.examsResults", { defaultValue: "Exams & Results" })} testId="student360-exams">
          <div className="space-y-2">
            {exam_results.map((r, i) => (
              <div key={i} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-slate-50">
                <div>
                  <p className="font-heading font-bold text-sm">{r.exam}</p>
                  <p className="text-xs text-slate-500">{r.subject}{r.date ? ` · ${fmtDate(r.date)}` : ""}</p>
                </div>
                <span className="font-heading font-extrabold text-brand-coralDark">{r.marks ?? "—"}/{r.max_marks ?? "—"}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Fees */}
      <Section title={t("student360.fees", { defaultValue: "Fees" })} testId="student360-fees">
        <div className="grid grid-cols-3 gap-2.5">
          <Tile label={t("student360.totalInvoiced", { defaultValue: "Total Invoiced" })} value={inr(fees?.invoiced ?? 0)} testId="student360-invoiced" />
          <Tile label={t("student360.totalPaid", { defaultValue: "Total Paid" })} value={inr(fees?.paid ?? 0)} testId="student360-paid" />
          <Tile label={t("student360.balanceDue", { defaultValue: "Balance Due" })} value={inr(fees?.due ?? 0)} testId="student360-due" />
        </div>
        {fees?.invoices?.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs text-slate-400 font-semibold">{t("student360.invoices", { defaultValue: "Invoices" })}</p>
            {fees.invoices.map((inv) => (
              <div key={inv.id} className="flex justify-between items-center text-sm p-2.5 rounded-lg bg-slate-50" data-testid={`student360-invoice-${inv.id}`}>
                <div>
                  <p className="font-semibold">{inv.title}</p>
                  <p className="text-xs text-slate-500">{t("student360.dueOn", { defaultValue: "Due" })} {fmtDate(inv.due_date)}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold">{inr(inv.amount)}</p>
                  <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-full ${inv.status === "paid" ? STATUS_STYLE.present : inv.status === "partial" ? STATUS_STYLE.leave : STATUS_STYLE.absent}`}>{inv.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {fees?.payments?.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-xs text-slate-400 font-semibold">{t("student360.paymentHistory", { defaultValue: "Payment History" })}</p>
            {fees.payments.map((p) => (
              <div key={p.id} className="flex justify-between items-center gap-3 text-sm p-2.5 rounded-lg bg-slate-50" data-testid={`student360-payment-${p.id}`}>
                <div>
                  <p className="font-bold">{inr(p.amount)}</p>
                  <p className="text-xs text-slate-500">{(p.method || "").replaceAll("_", " ").toUpperCase()} · {fmtDate(p.payment_date || p.created_at)}{p.receipt_no ? ` · ${p.receipt_no}` : ""}</p>
                </div>
                {variant === "parent" && (
                  <Button variant="outline" size="sm" data-testid={`student360-receipt-${p.id}`} disabled={receiptBusy === p.id} onClick={() => downloadReceipt(p.id)} className="rounded-full border-slate-300 text-xs font-bold px-3 shrink-0">
                    <DownloadSimple size={13} className="mr-1" />
                    {receiptBusy === p.id ? t("parentFees.preparing", { defaultValue: "Preparing…" }) : t("student360.receipt", { defaultValue: "Receipt" })}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Documents */}
      <Section title={t("student360.documents", { defaultValue: "Documents" })} testId="student360-documents">
        <Button variant="outline" data-testid="student360-report-card-btn" onClick={() => setReportOpen(true)} className="rounded-full font-bold border-slate-300">
          <FileText size={15} className="mr-1.5" /> {t("student360.viewReportCard", { defaultValue: "View Report Card" })}
        </Button>
      </Section>

      {reportOpen && <ReportCard studentId={studentId} onClose={() => setReportOpen(false)} />}
    </div>
  );
}
